export type QuoteFormat =
	| 'NO_QUOTE'
	| 'SINGLE_QUOTE'
	| 'DOUBLE_QUOTE'
	| 'BACK_TICK';

export class EnclosingHelpers {
	public static SINGLE_QUOTE: QuoteFormat = 'SINGLE_QUOTE';
	public static DOUBLE_QUOTE: QuoteFormat = 'DOUBLE_QUOTE';
	public static BACK_TICK: QuoteFormat = 'BACK_TICK';
	public static NO_QUOTE: QuoteFormat = 'NO_QUOTE';

	public static fmt2char(quoteFormat = EnclosingHelpers.DOUBLE_QUOTE) {
		switch (quoteFormat) {
			case EnclosingHelpers.NO_QUOTE:
				return '';
			case EnclosingHelpers.SINGLE_QUOTE:
				return '\'';
			case EnclosingHelpers.BACK_TICK:
				return '`';
			case EnclosingHelpers.DOUBLE_QUOTE:
			default:
				return '"';
		}
	}
	public static encloseField(str: string, quoteFormat: QuoteFormat): string {
		if (!str) return str;
		const quoteChar = EnclosingHelpers.fmt2char(quoteFormat);
		if (quoteChar == '' || EnclosingHelpers.isEnclosed(str, quoteChar)) {
			return str;
		}
		return EnclosingHelpers.encloseUsingQuoteValue(str, quoteChar);
	}

	private static encloseUsingQuoteValue = (
		str: string,
		quoteValue: string,
	): string => {
		if (quoteValue === '"') {
			return EnclosingHelpers.encloseDoubleQuote(str);
		} else if (quoteValue === '\'') {
			return EnclosingHelpers.encloseSingleQuote(str);
		} else if (quoteValue === '`') {
			return EnclosingHelpers.encloseBackTick(str);
		}
		return str;
	};

	public static isDoubleQuotesEnclosed = (str: string): boolean => {
		if (!str) return false;
		return str[0] === '"' && str[str.length - 1] === '"';
	};

	public static isSingleQuotesEnclosed = (str: string): boolean => {
		if (!str) return false;
		return str[0] === '\'' && str[str.length - 1] === '\'';
	};

	public static isBackTickEnclosed = (str: string): boolean => {
		if (!str) return false;
		return str[0] === '`' && str[str.length - 1] === '`';
	};

	public static isEnclosedInBrackets = (str: string): boolean => {
		if (!str) return false;
		return str[0] === '[' && str[str.length - 1] === ']';
	};

	public static isEnclosed = (str: string, quoteValue: string): boolean => {
		if (!str) return false;
		return str[0] === quoteValue && str[str.length - 1] === quoteValue;
	};

	public static removeEnclosingSingleQuotes = (str: string): string => {
		let ret = str;
		if (EnclosingHelpers.isSingleQuotesEnclosed(str))
			ret = str.substring(1, str.length - 1);
		return ret;
	};

	public static removeEnclosingDoubleQuotes = (str: string): string => {
		let ret = str;
		if (EnclosingHelpers.isDoubleQuotesEnclosed(str))
			ret = str.substring(1, str.length - 1);
		return ret;
	};

	public static removeEnclosingBrackets = (str: string): string => {
		let ret = str;
		if (EnclosingHelpers.isEnclosedInBrackets(str))
			ret = str.substring(1, str.length - 1);
		return ret;
	};

	public static removeEnclosingBackTick = (str: string): string => {
		let ret = str;
		if (EnclosingHelpers.isBackTickEnclosed(str))
			ret = str.substring(1, str.length - 1);
		return ret;
	};

	public static encloseDoubleQuote = (str: string): string => {
		const doubleQuoted = EnclosingHelpers.isDoubleQuotesEnclosed(str);
		const squareBracketed = EnclosingHelpers.isEnclosedInBrackets(str);
		if (doubleQuoted || squareBracketed) return str;
		const cleanedStr = EnclosingHelpers.removeEnclosingBrackets(
			EnclosingHelpers.removeEnclosingDoubleQuotes(str),
		);
		return `"${cleanedStr}"`;
	};

	public static encloseSingleQuote = (str: string): string => {
		const singleQuoted = EnclosingHelpers.isSingleQuotesEnclosed(str);
		if (singleQuoted) return str;
		return `'${str}'`;
	};

	public static encloseBackTick = (str: string): string => {
		const backTick = EnclosingHelpers.isBackTickEnclosed(str);
		if (backTick) return str;
		const cleanedStr = EnclosingHelpers.removeEnclosingBrackets(
			EnclosingHelpers.removeEnclosingDoubleQuotes(str),
		);
		let response = '';
		response += '`';
		response += cleanedStr;
		response += '`';
		return response;
	};
}
